import {useQuery, type OperationVariables} from '@apollo/client';
import type {DocumentNode} from 'graphql';
import {useCallback, useMemo, useRef} from 'react';

import {REST_GRAPHQL_CLIENTNAME} from '@famly/stat_apollo-client';

import type {FamlyQueryOptions, FamlyQueryResult} from './apollo-types';

/**
 * Simple wrapper for apollo's `useQuery`.
 *
 * 1) enhances the `fetchMore` to handle an edge case, where backend calls returning after a component was
 * unmounted resulting in errors making the CI tests flaky and confusing for everyone.
 * Github issue: {@link https://shorturl.at/aDFGJ}
 *
 *
 * 2) runs a query's `onCompleted` once per result rather than once per delivery — see
 * {@link useOncePerResult}.
 *
 * A map function passed to `updateQuery` returns without writing when `options.complete` is false: the cache holds
 * no complete result for the query yet, so `options.previousData` may be partial. `useStatQuery` instead holds such
 * an update back and applies it once the data arrives.
 *
 * @param query first parameter for the useQuery
 * @param options options parameter for the useQuery
 * @returns a more error prone and enhanced version of the return values
 */
export function useResilientQuery<TData = any, TVariables extends OperationVariables = OperationVariables>(
    query: DocumentNode,
    options?: FamlyQueryOptions<TData, TVariables>,
): FamlyQueryResult<TData, TVariables> {
    const reportsBackgroundRequests = options?.notifyOnNetworkStatusChange === true;
    const onCompleted = useOncePerResult(options?.onCompleted, reportsBackgroundRequests);

    return useWrapQueryResult(useQuery(query, options && {...options, onCompleted}));
}

/**
 * Keeps `onCompleted` firing once per *result* rather than once per delivery.
 *
 * Apollo can hand the same result to `onCompleted` more than once — on a redelivery, and on every
 * poll and revalidation for a query that reports background requests. A callback that dispatches or
 * sets state then does so again for a result it has already handled, and the re-render that follows
 * lands while the user is mid-interaction. The cache returns the same object when nothing changed,
 * so identity is what separates a genuinely new result from the same one delivered again.
 *
 * A call site that passes `notifyOnNetworkStatusChange: true` wants each delivery and keeps its
 * callback untouched.
 */
function useOncePerResult<TData>(
    onCompleted: ((data: TData) => void) | undefined,
    reportsBackgroundRequests: boolean,
): ((data: TData) => void) | undefined {
    const lastHandled = useRef<TData | undefined>(undefined);

    const deduplicated = useCallback(
        (data: TData) => {
            if (data === lastHandled.current) {
                return;
            }
            lastHandled.current = data;
            onCompleted?.(data);
        },
        [onCompleted],
    );

    if (onCompleted === undefined) {
        return undefined;
    }
    return reportsBackgroundRequests ? onCompleted : deduplicated;
}

/**
 * Simple wrapper for apollo's `useQuery`.
 *
 * 1) enhances the `fetchMore` to handle an edge case, where backend calls returning after a component was
 * unmounted resulting in errors making the CI tests flaky and confusing for everyone.
 * Github issue: {@link https://shorturl.at/aDFGJ}
 *
 * 2) includes the correct client name for queries going to the famlyfacade wrapper, which allows querying rest
 * api using graphql
 *
 * @param query first parameter for the useQuery
 * @param options options parameter for the useQuery
 * @returns a more error prone and enhanced version of the return values
 */
export function useResilientRestQuery<TData = any, TVariables extends OperationVariables = OperationVariables>(
    query: DocumentNode,
    options?: FamlyQueryOptions<TData, TVariables>,
): FamlyQueryResult<TData, TVariables> {
    return useResilientQuery(query, {...options, context: {...options?.context, clientName: REST_GRAPHQL_CLIENTNAME}});
}

/**
 * Generic hook type for resilient query wrappers that accept variables as the first argument
 * and return the standard query result. Useful for higher-order utilities that need to
 * accept any of our generated "useXxxQuery" hooks that internally call useResilient(Rest)Query.
 */
export type ResilientVariablesQueryHook<TData, TVariables extends OperationVariables> = (
    variables: TVariables,
    options?: Omit<FamlyQueryOptions<TData, TVariables>, 'variables'>,
) => FamlyQueryResult<TData, TVariables>;

/**
 * Actual wrapper for the resilient query, which adds the correct error handling.
 *
 * @param queryResult the query result to be wrapped
 * @returns the more error prone query result
 */
function useWrapQueryResult<Q, QV extends OperationVariables>(
    queryResult: FamlyQueryResult<Q, QV>,
): FamlyQueryResult<Q, QV> {
    return useMemo(
        () => ({
            ...queryResult,
            updateQuery: mapFn => {
                try {
                    queryResult.updateQuery(mapFn);
                } catch (e: any) {
                    if (
                        !(e?.message as string)?.includes(`ObservableQuery with this id doesn't exist`) &&
                        !(e?.message as string)?.includes(`17`)
                    ) {
                        throw e;
                    }
                }
            },
            fetchMore: async options => {
                let result;
                try {
                    result = await queryResult.fetchMore(options);
                } catch (e: any) {
                    if (
                        !(e?.message as string)?.includes(`ObservableQuery with this id doesn't exist`) &&
                        !(e?.message as string)?.includes(`17`)
                    ) {
                        throw e;
                    }
                }
                return result as any;
            },
        }),
        [queryResult],
    );
}
